// 库存非标准操作指令：执行记录（含轮次、里程碑、客户回复）、收费行、终审结果附件

/** note：员工/管理员的普通记录；milestone：提交本轮结果给客户；customer_reply：客户回复 */
export type EntryKind = 'note' | 'milestone' | 'customer_reply';

export interface NonStdAttachment {
  url: string;
  name?: string;
}

export interface NonStdEntry {
  id: number;
  kind: EntryKind;
  /** 第几轮：每"提交本轮结果给客户"一次，轮次加 1 */
  round_no: number;
  text: string;
  /** 里程碑的附加信息 {round, entry_count, visible_count}；普通记录没有 */
  data?: Record<string, any>;
  attachments: NonStdAttachment[];
  /** 客户视角的条目没有这个字段 */
  customer_visible?: boolean;
  created_by_id?: number | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at?: string;
  history?: Array<{ at: string; by: number; action: string; before: Record<string, any> }>;
}

export interface NonStdEntryPayload {
  text?: string;
  attachments?: NonStdAttachment[];
  customer_visible?: boolean;
}

export type ChargeLineStatus = 'suggested' | 'confirmed' | 'voided';

export interface NonStdChargeLine {
  id: number;
  operation_type?: number;
  operation_type_name: string;
  operation_type_code?: string;
  description: string;
  quantity: string;
  billing_unit: string;
  /** 自由填写的展示单位（如“项”“次”），只用于显示；计费单位是 billing_unit */
  unit_label?: string;
  unit_price: string;
  currency: string;
  amount: string;
  /** 引用的收费规则名称：客户视角只在这条规则对客户后台可见时才有值 */
  rule_name?: string;
  /** 以下字段客户视角没有。price_source：rule=规则价，override=改了规则价，temporary=临时收费，manual=旧数据 */
  price_source?: 'rule' | 'override' | 'temporary' | 'manual';
  rule_id?: number | null;
  rule_level?: 'system' | 'customer' | 'project' | '';
  rule_code?: string;
  rule_visible?: boolean;
  rule_unit_price?: string | null;
  is_temporary?: boolean;
  /** 梯度规则按重量/体积分档时：梯度维度和确定档位用的值（和计费数量是两个独立的输入） */
  tier_basis?: string;
  tier_value?: string | null;
  price_reason?: string;
  status?: ChargeLineStatus;
  billing_record_id?: number | null;
  created_by_name?: string | null;
  void_reason?: string;
  created_at: string;
  /** 修改/作废已经生成过收费记录的行后，后端会提示需要重算 */
  requires_recalc?: boolean;
  /** 新增/修改后端给的提示（如固定收费规则的数量不在适用区间内），不拦 */
  warning?: string;
}

/** 新增时必须给 mode：rule=引用已有收费规则（rule_id），temporary=临时收费（名称、计费单位、单价、原因都要填） */
export interface NonStdChargeLinePayload {
  mode?: 'rule' | 'temporary';
  rule_id?: number;
  description?: string;
  quantity?: string | number;
  /** 梯度规则的维度不是数量时必填：确定档位用的值（如重量） */
  tier_value?: string | number | null;
  /** 引用规则时留空=按规则价；填了不同的价格就是改价，要写原因 */
  unit_price?: string | number | null;
  currency?: string;
  billing_unit?: string;
  unit_label?: string;
  price_reason?: string;
}

export interface NonStdRuleTier {
  min_value: string | null;
  max_value: string | null;
  unit_price: string;
}

/** 本项目此刻适用的收费规则（系统级/客户级/项目级） */
export interface NonStdApplicableRule {
  id: number;
  system_code: string;
  label: string;
  level: 'system' | 'customer' | 'project';
  level_text: string;
  operation_type_name: string;
  variant_key: string;
  billing_unit: string;
  billing_unit_text: string;
  unit_label: string;
  rule_type: 'flat' | 'tiered';
  /** 梯度维度：QUANTITY 数量 / WEIGHT_G / WEIGHT_KG / VOLUME_* / AGE_DAY；needs_tier_value=维度不是数量，要单独填值 */
  tier_basis: string;
  tier_basis_name: string;
  tier_basis_unit: string;
  needs_tier_value: boolean;
  /** 不能用于非标收费的规则（如按库龄分档），unusable_reason 说明原因 */
  usable: boolean;
  unusable_reason: string;
  unit_price: string;
  currency: string;
  tiers: NonStdRuleTier[];
  min_value: string | null;
  max_value: string | null;
  remark: string;
  visible_in_customer_portal: boolean;
}

export interface NonStdRoundStat {
  round: number;
  /** 员工记录数 / 其中对客户可见的 */
  entry_count: number;
  visible_count: number;
  reply_count: number;
  submitted: boolean;
}

export interface NonStdFinalAttachment {
  id: number;
  name: string;
  url: string;
  size?: number | null;
  created_at: string;
}

export interface NonStdSummary {
  instruction: { id: number; system_code: string; status: string; name?: string | null };
  can_edit: boolean;
  /** 客户可以回复（指令还在进行中） */
  can_reply: boolean;
  viewer: { is_operator: boolean; is_admin: boolean; can_view_charges: boolean };
  current_round: number;
  /** 最近一次提交给客户之后，客户还没有回复 */
  waiting_customer: boolean;
  entries: NonStdEntry[];
  /** 仓库员工拿到的是空数组：收费由管理员在终审时确定 */
  charge_lines: NonStdChargeLine[];
  confirmed_totals: Record<string, string>;
  /** 仅员工/管理员：按轮次的汇总 */
  rounds?: NonStdRoundStat[];
  /** 仅管理员 */
  final_attachments?: NonStdFinalAttachment[];
  /** 仅员工/管理员：容器标签清单、建议的下一个编号、当前能否打印（到终审完成为止） */
  labels?: NonStdLabel[];
  label_next_number?: string;
  /** 本指令用过的标识组（按第一次出现的顺序）和每组的下一个建议编号；最近一张标签用的组（新建时默认选它） */
  label_groups?: NonStdLabelGroupInfo[];
  label_last_group?: string;
  can_print_labels?: boolean;
  /** 仅员工/管理员："确认完成"阶段的状态、容器核对统计、补充说明 */
  confirm?: NonStdConfirmInfo;
  /** 仅员工/管理员：销毁记录（客户拿不到这个字段） */
  destroy?: NonStdDestroyInfo;
  /** 执行记录阶段已提交：记录和标签已固化为快照，任何人都不能再改；客户也不能再回复 */
  records_frozen?: boolean;
}

/** 销毁记录的合计：记录条数、件数、净重（克） */
export interface NonStdDestroyTotals {
  count: number;
  qty: number;
  net_weight_g: number;
}

/** 一条销毁记录：现场记录销毁了多少件、净重多少（克）；只是记录，不动库存 */
export interface NonStdDestroyRow {
  id: number;
  inventory_unit_id?: number | null;
  unit_code?: string;
  product_text: string;
  qty: number;
  /** 接口里是字符串，快照里是数字 */
  net_weight_g: number | string;
  remark: string;
  attachments: NonStdAttachment[];
  created_by_name?: string | null;
  created_at: string;
  edit_count?: number;
}

export interface NonStdDestroyUnitOption {
  id: number;
  code: string;
  content_items?: Array<{ name: string; qty: number }>;
}

export interface NonStdDestroyInfo {
  records: NonStdDestroyRow[];
  totals: NonStdDestroyTotals;
  /** 声明过"本指令没有发生销毁"；有了销毁记录会自动取消 */
  no_destroy_declared: boolean;
  no_destroy_by_name?: string | null;
  no_destroy_at?: string | null;
  /** 已回答销毁情况（有记录或有声明）：确认完成提交前必须回答 */
  answered: boolean;
  /** 现在能否记录（到确认完成提交为止，不受执行记录冻结影响） */
  can_edit: boolean;
  unit_options: NonStdDestroyUnitOption[];
}

export interface NonStdDestroyPayload {
  inventory_unit_id?: number | null;
  product_text?: string;
  qty?: number;
  net_weight_g?: number;
  remark?: string;
  attachments?: NonStdAttachment[];
}

/** 阶段快照：步骤提交时由后端固化，之后不再变化 */
interface NonStdSnapshotBase {
  version: number;
  submitted_at: string;
  submitted_by_id?: number | null;
  submitted_by_name?: string;
}

export interface NonStdRecordSnapshot extends NonStdSnapshotBase {
  stage: 'record';
  entries: Array<Partial<NonStdEntry> & { id: number; kind: EntryKind; round_no: number; text: string; attachments: NonStdAttachment[]; created_at: string; edit_count?: number }>;
  rounds: NonStdRoundStat[];
  voided_count: number;
  labels: Array<{
    id: number; number: string; contents: string; remark: string; created_by_name?: string | null; created_at: string; print_count: number;
  } & NonStdLabelExtra>;
  counts: { entries: number; rounds: number; labels: number };
  /** 功能上线前提交的旧快照没有销毁记录 */
  destroy_records?: NonStdDestroyRow[];
  destroy_totals?: NonStdDestroyTotals;
}

export interface NonStdConfirmSnapshot extends NonStdSnapshotBase {
  stage: 'confirm';
  note: string;
  containers: Array<{
    id: number; number: string; contents: string; remark: string; print_count: number;
    labeled: boolean; location: string; status: PlacementStatus; unused_reason: string;
  } & NonStdLabelExtra>;
  counts: NonStdContainerCounts;
  /** 功能上线前提交的旧快照没有销毁记录和声明 */
  destroy_records?: NonStdDestroyRow[];
  destroy_totals?: NonStdDestroyTotals;
  no_destroy_declared?: boolean;
  no_destroy_by_name?: string | null;
  no_destroy_at?: string | null;
}

/** "拣货"阶段（实际提交过拣货才有；拣货改成必做之前创建的指令可以跳过）：data 是拣货的完整快照（和常规指令的拣货快照同一份：stats、pick_logs、ui_context、目标数量/标识映射等） */
export interface NonStdPickSnapshot extends NonStdSnapshotBase {
  stage: 'pick';
  data: Record<string, any>;
}

export type NonStdSnapshot = NonStdRecordSnapshot | NonStdConfirmSnapshot | NonStdPickSnapshot;

/** 终审页"各阶段快照"表格的一行：每个自定义阶段一行 */
export interface NonStdStageSnapshotItem {
  key: 'pick' | 'record' | 'confirm';
  title: string;
  stage: number;
  /** pending / in_progress / done */
  status: string;
  finished_at?: string | null;
  submitted_by_name: string;
  /** 阶段已提交但没有快照（功能上线前提交的旧数据）时为 null */
  snapshot: NonStdSnapshot | null;
}

export interface NonStdContainerCounts {
  total: number;
  /** 已就位：已贴标签且记录了位置 */
  placed: number;
  unused: number;
  /** 已消耗：容器里的东西已被二次加工、容器已不存在（不需要贴标签和位置） */
  consumed: number;
  /** 待处理：既不是已就位、未使用，也不是已消耗 */
  pending: number;
  unlabeled: number;
  no_location: number;
}

export interface NonStdConfirmInfo {
  /** 阶段 2（执行记录）、阶段 3（确认完成）是否已提交 */
  record_done: boolean;
  submit_done: boolean;
  /** 现在能否核对容器、写补充说明（阶段 2 已提交、阶段 3 未提交） */
  can_place: boolean;
  /** 现在能否标记/取消"已消耗"（指令进行中、确认完成还没提交；阶段 2 冻结后也可以） */
  can_consume?: boolean;
  note: string;
  note_updated_at?: string | null;
  containers: NonStdContainerCounts;
}

export type PlacementStatus = 'pending' | 'placed' | 'unused' | 'consumed';

export interface NonStdPlacementPayload {
  labeled?: boolean;
  location?: string;
  unused?: boolean;
  unused_reason?: string;
}

export interface NonStdLabelPrintLog {
  at: string;
  by: number;
  copies: number;
  /** agent：打印代理；browser：浏览器打印 */
  channel: 'agent' | 'browser';
}

/** 标签/容器多出来的信息（都是选填）：标识组、尺寸（cm）、重量（g）、所选预设包材、是否已消耗。小数在接口里可能是字符串 */
export interface NonStdLabelExtra {
  group?: string;
  length_cm?: number | string | null;
  width_cm?: number | string | null;
  height_cm?: number | string | null;
  weight_g?: number | string | null;
  packaging_code?: string;
  packaging_name?: string;
  consumed?: boolean;
}

export interface NonStdLabelGroupInfo {
  name: string;
  /** 这个组的下一个建议编号 */
  next_number: string;
  count: number;
  consumed_count: number;
}

/** 可选的预设发货包材（本指令所在仓库的仓库所属包材 + 本指令客户的客户所属包材） */
export interface NonStdPackaging {
  system_code: string;
  name: string;
  packaging_type_display: string;
  ownership_type: 'warehouse' | 'customer';
  ownership_display: string;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
}

/** 容器标签：编号、内容物、备注都可以留空；固定内容（指令编码、项目、操作人、打印时间）打印时由系统生成 */
export interface NonStdLabel extends NonStdLabelExtra {
  id: number;
  number: string;
  contents: string;
  remark: string;
  created_by_name?: string | null;
  created_at: string;
  updated_at?: string;
  print_count: number;
  last_printed_at?: string | null;
  last_printed_by_name?: string | null;
  print_log?: NonStdLabelPrintLog[];
  /** "确认完成"阶段的容器核对：已贴标签、放置位置（手填文字）、未使用 */
  labeled?: boolean;
  labeled_at?: string | null;
  location?: string;
  placed_at?: string | null;
  unused?: boolean;
  unused_reason?: string;
  placement_status?: PlacementStatus;
  consumed_by_name?: string | null;
  consumed_at?: string | null;
}

export interface NonStdLabelPayload {
  group?: string;
  number?: string;
  contents?: string;
  remark?: string;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  weight_g?: number | null;
  packaging_code?: string;
  packaging_name?: string;
}

/** 按所选规则和数量求出的单价（梯度规则按数量匹配档位） */
export interface PriceHint {
  found: boolean;
  /** 梯度规则按重量/体积分档，还没填档位依据值，没法求单价 */
  need_tier_value?: boolean;
  unit_price?: string;
  currency?: string;
  rule_id?: number;
  billing_unit?: string | null;
  unit_label?: string;
  tier?: NonStdRuleTier | null;
  /** 固定收费规则的数量不在适用区间内时的提示（不拦） */
  warning?: string;
}

/** 计费单位（和收费规则的计费单位一致） */
export const NONSTD_BILLING_UNITS: Array<{ value: string; label: string }> = [
  { value: 'PER_ITEM', label: '按件' },
  { value: 'PER_BOX', label: '按箱' },
  { value: 'PER_PALLET', label: '按托盘' },
  { value: 'PER_LB', label: '按磅' },
  { value: 'PER_KG', label: '按千克' },
  { value: 'PER_CUFT', label: '按立方英尺' },
  { value: 'PER_CBM', label: '按立方米' },
  { value: 'PER_CC', label: '按立方厘米' },
  { value: 'PER_VALUE', label: '按价值' },
  { value: 'PER_ORDER', label: '按订单' },
  { value: 'PER_MINUTE', label: '按分钟' },
];

export const NONSTD_RULE_LEVEL_TEXT: Record<string, string> = { project: '项目级', customer: '客户级', system: '系统级' };

export const ENTRY_KIND_LABEL: Record<EntryKind, string> = {
  note: '仓库记录',
  milestone: '提交给客户',
  customer_reply: '客户回复',
};

export const CHARGE_STATUS_LABEL: Record<ChargeLineStatus, string> = {
  suggested: '建议（待管理员确认）',
  confirmed: '已确认',
  voided: '已作废',
};

// ---- 终审库存结算（仅管理员） ----
/**
 * 一个锁定的库存单元的处理方式：数量分成 保持原样 / 转化为其他库存 / 销毁三部分，三者之和 = 锁定数量。
 * 锁定处理由数量推导：转化和销毁一定转消耗；保持原样里 hold_qty 件转业务留存占用，其余释放。
 */
export interface SettlementInput {
  iu_id: number;
  keep_qty: number;
  convert_qty: number;
  destroy_qty: number;
  hold_qty: number;
}

export interface SettlementOutput {
  kind: 'unit' | 'aiu';
  iu_id: number | null;
  project_id?: number | null;
  qty: number;
  /** 手动指定库龄（默认按输入层数量加权）；指定时必须写原因 */
  age_override_at?: string | null;
  age_override_reason?: string;
  /** 转入 AIU 时必填：这批 AIU 的总重量（克），用来按数量加权更新 AIU 的平均重量 */
  weight_g?: number | null;
}

/** 一个处理组：组里每个库存单元的转化数量合起来，变成 outputs 里的转化目标（多个源合成一个目标 = 组合，一个源转成多个目标 = 拆分） */
export interface SettlementLine {
  id?: number;
  seq?: number;
  note?: string;
  inputs: SettlementInput[];
  outputs: SettlementOutput[];
}

export interface SettlementUnitInfo {
  id: number;
  code: string;
  unit_type: string;
  project_id: number | null;
  project_code: string;
  sorting_group: string;
  /** 内容物：散货一种产品，箱/托盘可能有多种，异常库存单元没有 */
  content_items?: Array<{ name: string; qty: number; code?: string; barcodes?: string[] }>;
  on_hand?: number;
}

export interface SettlementPoolRow {
  iu_id: number;
  /** 本指令对这个库存单元尚未处理的有效锁定量 */
  reserved_qty: number;
  /** 拣货记录里拣出的数量（多次提交加起来）；指令没有拣货提交（旧指令跳过了拣货）时为 null */
  picked_qty?: number | null;
  kinds: string[];
  code?: string;
  unit_type?: string;
  project_code?: string;
  sorting_group?: string;
  content_items?: Array<{ name: string; qty: number; barcodes?: string[] }>;
}

export interface SettlementIssue {
  code: string;
  message: string;
  line?: number | null;
}

export interface SettlementActionRow {
  type: 'consume' | 'release' | 'reserve_hold' | 'produce';
  line: number | null;
  iu_id: number | null;
  iu_code: string;
  qty: number;
  why?: string;
  received_at?: string;
  age_source?: 'weighted' | 'override';
  reservation_id?: number;
  /** 转入 AIU：这批的总重量和 AIU 平均重量（克/件）的变化 */
  weight_g?: number;
  avg_weight_before?: number;
  avg_weight_after?: number;
}

export interface SettlementPreview {
  empty: boolean;
  message?: string;
  blockers: SettlementIssue[];
  warnings: SettlementIssue[];
  /** 只是提示，不需要确认（例如没设置处理方式的锁定会按默认释放） */
  notices?: SettlementIssue[];
  actions: SettlementActionRow[];
  units: Array<{
    iu_id: number | null; iu_code: string; unit_type: string; new_unit: boolean;
    on_hand_before: number; on_hand_after: number; reserved_before: number; reserved_after: number;
    /** 是否是本指令的操作对象（有锁定的变化）/ 是否是转化目标（有新增的数量） */
    is_source: boolean;
    is_target: boolean;
    project_id?: number | null;
    project_code?: string;
    /** 库存单元的内容项（产品名、数量、分拣条码） */
    content_items?: Array<{ name: string; qty: number; barcodes?: string[] }>;
    /** 操作对象：只算本指令锁定的那部分（不是这个库存单元的全部库存）：锁定数，以及其中 不变/转化/销毁 各多少、几件转业务留存占用 */
    source: { locked: number; keep: number; convert: number; destroy: number; hold: number; picked?: number | null } | null;
    /** 转化目标新增的库存：数量、库龄（入库层时间）、AIU 的总重量和平均重量变化 */
    produced: Array<{ qty: number; received_at: string; age_source: 'weighted' | 'override'; weight_g?: number; avg_weight_before?: number; avg_weight_after?: number }>;
  }>;
  /** 所有操作对象库存单元的汇总（只算本指令锁定的那部分）：锁定数 = 不变 + 转化 + 销毁；锁定处理 = 释放 + 转业务留存占用 + 消耗 */
  source_totals?: { units: number; locked: number; keep: number; convert: number; destroy: number; hold: number; release: number; consume: number };
  leftover: Array<{ iu_id: number; iu_code: string; qty: number }>;
  summary: { consume_qty?: number; release_qty?: number; hold_qty?: number; produce_qty?: number; actions?: number };
  fingerprint: string;
  /** 终审容器分配的指纹和对账表；没有建容器草稿时指纹是空 */
  container_fingerprint?: string;
  /** list：这次预览用的容器草稿（在预览里展示最终的容器） */
  containers?: { engaged: boolean; ledger: ContainerLedgerRow[]; list?: ContainerDraft[] };
}

export interface SettlementDraft {
  id: number;
  status: 'draft' | 'applied';
  version: number;
  confirmed: boolean;
  confirmed_at?: string | null;
  confirm_note?: string;
  applied_at?: string | null;
  apply_result?: { summary?: SettlementPreview['summary']; actions?: SettlementActionRow[]; warnings?: SettlementIssue[]; units?: SettlementPreview['units'] };
  lines: SettlementLine[];
}

export interface SettlementOverview {
  supported: boolean;
  unsupported_reason: string;
  instruction_status: string;
  can_edit: boolean;
  settlement: SettlementDraft | null;
  pool: SettlementPoolRow[];
  units: Record<string, SettlementUnitInfo>;
  /** 本指令涉及的每个项目的 AIU 现状：已有就给出信息，没有是 null（提交终审时会自动创建） */
  aiu_options?: Array<{ project_id: number; project_code: string; aiu: SettlementUnitInfo | null }>;
}

// ---------------- 终审容器分配（P2） ----------------
export type ContainerKind = 'sc' | 'ioc';

export interface ContainerItemDraft {
  iu_id?: number | null;
  /** 项目 AIU 还不存在（结算应用时才创建）时用项目指定 */
  aiu_project_id?: number | null;
  qty: number;
  remarks?: string;
}

export interface ContainerDraft {
  id?: number;
  seq?: number;
  kind: ContainerKind;
  container_type: string;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  weight_g?: number | null;
  /** 仓位：一段文字（仓位编码或临时位置） */
  location_text?: string;
  /** 系统解析的结果：formal = 匹配上本仓库的正式仓位，text = 临时文字，空 = 没填 */
  location_match?: 'formal' | 'text' | '';
  location_code?: string;
  remarks?: string;
  reference_label_id?: number | null;
  applied_container_id?: number | null;
  applied_container_code?: string;
  items: ContainerItemDraft[];
}

/** 对账表一行：一个库存单元需要分配到新容器的数量和已分配的数量 */
export interface ContainerLedgerRow {
  unit_key: string;
  iu_id: number | null;
  iu_code: string;
  unit_type: string;
  new_unit: boolean;
  project_id?: number | null;
  project_code?: string;
  content_items?: Array<{ name: string; qty: number; code?: string; barcodes?: string[] }>;
  /** 本指令锁定数、拣出数（null = 没有拣货记录）、转化、销毁 */
  locked: number;
  picked: number | null;
  convert: number;
  destroy: number;
  /** 拣出后保留的数量、转化目标新增的数量、需要分配的总量 */
  kept: number;
  produced: number;
  total: number;
  /** 转业务留存占用的数量：IOC 里必须正好是这么多 */
  hold: number;
  sc_qty: number;
  ioc_qty: number;
  unassigned: number;
  /** 分拣组：SC 里的库存单元必须同组 */
  group?: string;
}

export interface ContainersView {
  supported: boolean;
  unsupported_reason: string;
  version: number;
  can_edit: boolean;
  applied: boolean;
  /** 已应用时：ledger 是提交终审当时保存的对账快照（不再按当前库存重新对账，所以也没有阻断和警告）；
   * ledger_snapshot_missing = 提交时还没有保存快照的旧数据，对账表为空 */
  ledger_snapshot?: boolean;
  ledger_snapshot_missing?: boolean;
  warehouse_code?: string;
  containers: ContainerDraft[];
  /** 是否建了容器草稿；没建时不检查容器账 */
  engaged: boolean;
  ledger: ContainerLedgerRow[];
  blockers: SettlementIssue[];
  warnings: SettlementIssue[];
  fingerprint: string;
}
